import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box,
    Card,
    Table,
    Button,
    Tooltip,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Container,
    IconButton,
    TableContainer,
    TablePagination,
    Typography,
    Chip,
    Badge,
    Stack,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Grid,
    Paper,
    useTheme,
    Alert,
    CircularProgress,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import {
    FilterList as FilterIcon,
    Refresh as RefreshIcon,
    Info as InfoIcon,
    Visibility as VisibilityIcon,
    TrendingUp,
    TrendingDown,
    Warning,
    ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import Page from '../../../components/Page';
import HeaderBreadcrumbs from '../../../components/HeaderBreadcrumbs';
import useSettings from '../../../hooks/useSettings';
import Scrollbar from '../../../components/Scrollbar';
import { TableHeadCustom } from '../../../components/table';
import { getSuspiciousTicks } from '../../../redux/slices/operator';
import SuspiciousTicksFiltersModal from './components/SuspiciousTicksFiltersModal';
import AlertTypesInfoModal from './components/AlertTypesInfoModal';
import SuspiciousTickDetailsModal from './components/SuspiciousTickDetailsModal';

// Alert types with descriptions
export const ALERT_TYPES = [
    // Market Manipulation
    { type: "WASH_TRADE", description: "Multiple trades at the same price in a short period" },
    { type: "DETAILED_WASH_TRADE", description: "Detailed analysis of wash trade pattern" },
    { type: "ARTIFICIAL_PRICE_MOVE", description: "Price change without corresponding volume" },
    { type: "PUMP_AND_DUMP", description: "Artificially inflating price then selling at peak" },
    { type: "BEAR_RAID", description: "Aggressive short selling to drive down prices" },

    // Order Book Manipulation
    { type: "SPOOFING_BID", description: "Large bid orders placed and quickly cancelled to manipulate prices upward" },
    { type: "SPOOFING_ASK", description: "Large ask orders placed and quickly cancelled to manipulate prices downward" },
    { type: "SPOOFING_BID_CANCEL", description: "Detection of cancelled bid orders that were likely spoofing attempts" },
    { type: "SPOOFING_ASK_CANCEL", description: "Detection of cancelled ask orders that were likely spoofing attempts" },
    { type: "LAYERING_BID", description: "Multiple bid orders at different price levels creating false buying interest" },
    { type: "LAYERING_ASK", description: "Multiple ask orders at different price levels creating false selling pressure" },
    { type: "QUOTE_STUFFING", description: "High-frequency placement and cancellation of orders to slow down competitors" },

    // Data Quality Issues
    { type: "PRICE_OUT_OF_RANGE", description: "Trade prices that fall outside expected market ranges" },
    { type: "TRADE_OUTSIDE_SPREAD", description: "Trades executed outside the current bid-ask spread" },
    { type: "TIME_SEQUENCE_ERROR", description: "Trades with incorrect or out-of-sequence timestamps" },
    { type: "TIME_GAP", description: "Unusual gaps in trade timing that may indicate data issues" },
    { type: "INVALID_DATA", description: "Trade data that contains invalid or corrupted information" },

    // Volume Anomalies
    { type: "VOLUME_ANOMALY", description: "Unusual trading volume without corresponding price impact" },

    // Legacy/Generic Types
    { type: "SPOOFING", description: "Large orders placed and quickly cancelled to manipulate prices" },
    { type: "LAYERING", description: "Multiple orders at different price levels to create false impression" },
    { type: "MOMENTUM_IGNITION", description: "Small trades triggering algorithmic responses" },
    { type: "BANGING_THE_CLOSE", description: "Trading near market close to manipulate closing prices" },
    { type: "CORNER_SQUEEZE", description: "Attempt to control supply to manipulate prices" },
    { type: "MARKING_THE_CLOSE", description: "Trading to influence closing prices" },
    { type: "CROSS_PRODUCT_MANIPULATION", description: "Manipulation across related financial instruments" },
    { type: "HIGH_CLOSING", description: "Pushing prices higher at market close" }
];

// Table headers
const TABLE_HEAD = [
    { id: 'script', label: 'Instrument', align: 'left' },
    { id: 'price', label: 'Price', align: 'right' },
    { id: 'timestamp', label: 'Time', align: 'center' },
    { id: 'alertType', label: 'Alert Type', align: 'center' },
    { id: 'direction', label: 'Direction', align: 'center' },
    { id: 'severity', label: 'Severity', align: 'center' },
    { id: 'description', label: 'Description', align: 'left' },
    { id: 'actions', label: 'Actions', align: 'center' },
];

// Severity color mapping
const getSeverityColor = (severity) => {
    switch (severity?.toLowerCase()) {
        case 'high': return 'error';
        case 'medium': return 'warning';
        case 'low': return 'info';
        default: return 'default';
    }
};

// Alert type color mapping
const getAlertTypeColor = (alertType) => {
    if (alertType?.includes('WASH_TRADE')) return 'error';
    if (alertType?.includes('SPOOFING')) return 'warning';
    if (alertType?.includes('LAYERING')) return 'info';
    if (alertType?.includes('PUMP') || alertType?.includes('DUMP') || alertType?.includes('RAID')) return 'error';
    return 'default';
};

// Helper function to render table content
const renderTableContent = (isLoading, paginatedData, TABLE_HEAD, getAlertTypeColor, getSeverityColor, handleRowClick) => {
    if (isLoading) {
        return (
            <TableRow>
                <TableCell colSpan={TABLE_HEAD.length} align="center" sx={{ py: 8 }}>
                    <CircularProgress size={32} />
                    <Typography variant="body2" sx={{ mt: 1.5 }} fontSize="0.875rem">
                        Loading suspicious ticks...
                    </Typography>
                </TableCell>
            </TableRow>
        );
    }

    if (paginatedData.length === 0) {
        return (
            <TableRow>
                <TableCell colSpan={TABLE_HEAD.length} align="center" sx={{ py: 8 }}>
                    <Typography variant="body2" color="text.secondary" fontSize="0.875rem">
                        No suspicious ticks found
                    </Typography>
                </TableCell>
            </TableRow>
        );
    }

    return paginatedData.map((tick, index) => (
        <TableRow
            key={tick.id || index}
            hover
            sx={{
                '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.02)',
                    '& .action-button': { opacity: 1 }
                },
                '& .action-button': { opacity: 0.7 },
                height: 48
            }}
        >
            <TableCell sx={{ py: 0.75 }}>
                <Stack spacing={0.25}>
                    <Typography variant="body2" fontWeight="600" fontSize="0.75rem" lineHeight={1.2}>
                        {tick.script}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" fontSize="0.6875rem" lineHeight={1.1}>
                        {tick.segment}
                    </Typography>
                </Stack>
            </TableCell>
            <TableCell align="right" sx={{ py: 0.75 }}>
                <Typography
                    variant="body2"
                    fontWeight="600"
                    fontSize="0.75rem"
                    color="primary.main"
                >
                    {tick.priceFormatted}
                </Typography>
            </TableCell>
            <TableCell align="center" sx={{ py: 0.75 }}>
                <Typography variant="body2" fontSize="0.75rem">
                    {tick.timestampFormatted}
                </Typography>
            </TableCell>
            <TableCell align="center" sx={{ py: 0.75 }}>
                <Tooltip title={ALERT_TYPES.find(at => at.type === tick.alertType)?.description || ''}>
                    <Chip
                        label={tick.alertType?.replace(/_/g, ' ')}
                        size="small"
                        color={getAlertTypeColor(tick.alertType)}
                        variant="outlined"
                        sx={{
                            fontSize: '0.6875rem',
                            height: 20,
                            cursor: 'help'
                        }}
                    />
                </Tooltip>
            </TableCell>
            <TableCell align="center" sx={{ py: 0.75 }}>
                <Typography variant="body2" fontSize="0.75rem">
                    {tick.direction || 'N/A'}
                </Typography>
            </TableCell>
            <TableCell align="center" sx={{ py: 0.75 }}>
                <Chip
                    label={tick.severityLevel?.toUpperCase()}
                    size="small"
                    color={getSeverityColor(tick.severityLevel)}
                    sx={{
                        fontSize: '0.6875rem',
                        height: 20,
                        fontWeight: 600
                    }}
                />
            </TableCell>
            <TableCell sx={{ py: 0.75 }}>
                <Tooltip title={tick.description || 'No description available'}>
                    <Typography
                        variant="body2"
                        fontSize="0.75rem"
                        sx={{
                            maxWidth: 350,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            cursor: 'help'
                        }}
                    >
                        {tick.description || 'No description available'}
                    </Typography>
                </Tooltip>
            </TableCell>
            <TableCell align="center" sx={{ py: 0.75 }}>
                <Tooltip title="View Details">
                    <IconButton
                        className="action-button"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleRowClick(tick);
                        }}
                        size="small"
                        color="primary"
                        sx={{
                            transition: 'all 0.2s',
                            '&:hover': {
                                backgroundColor: 'primary.main',
                                color: 'white'
                            }
                        }}
                    >
                        <VisibilityIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </TableCell>
        </TableRow>
    ));
};

const SourceSuspiciousTicksList = () => {
    const { themeStretch } = useSettings();
    const theme = useTheme();
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();

    // Redux state
    const { suspiciousTicks, isLoading } = useSelector((state) => state.operator);

    // Local state
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(50);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [infoModalOpen, setInfoModalOpen] = useState(false);
    const [detailsModalOpen, setDetailsModalOpen] = useState(false);
    const [selectedTick, setSelectedTick] = useState(null);
    const [appliedFilters, setAppliedFilters] = useState(() => {
        const saved = localStorage.getItem('suspiciousTicksFilters');
        return saved ? JSON.parse(saved) : {};
    });

    // Load data on component mount and when filters change
    const loadSuspiciousTicks = useCallback(async () => {
        try {
            const result = await dispatch(getSuspiciousTicks({
                limit: rowsPerPage,
                page: page + 1, // API expects 1-based page numbers
                ...appliedFilters
            }));

            if (!result.success) {
                enqueueSnackbar(result.error || 'Failed to load suspicious ticks', { variant: 'error' });
            }
        } catch (error) {
            enqueueSnackbar('Error loading suspicious ticks', { variant: 'error' });
        }
    }, [dispatch, appliedFilters, rowsPerPage, page, enqueueSnackbar]);

    useEffect(() => {
        loadSuspiciousTicks();
    }, [loadSuspiciousTicks]);

    // Handle pagination
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        const newRowsPerPage = parseInt(event.target.value, 10);
        setRowsPerPage(newRowsPerPage);
        setPage(0); // Reset to first page when changing page size
    };

    // Handle filters
    const handleApplyFilters = (newFilters) => {
        setAppliedFilters(newFilters);
        localStorage.setItem('suspiciousTicksFilters', JSON.stringify(newFilters));
        setPage(0);
        setFiltersOpen(false);
    };

    // Handle refresh
    const handleRefresh = () => {
        loadSuspiciousTicks();
    };

    // Handle row click
    const handleRowClick = (tick) => {
        setSelectedTick(tick);
        setDetailsModalOpen(true);
    };

    // Process data with memoization for performance
    const processedData = useMemo(() => {
        const ticksData = Array.isArray(suspiciousTicks?.data) ? suspiciousTicks.data : [];
        return ticksData.map((tick) => ({
            ...tick,
            priceFormatted: typeof tick.price === 'number' ? tick.price.toFixed(2) : tick.price,
            timestampFormatted: tick.timestamp ? format(new Date(tick.timestamp), 'MMM dd, HH:mm:ss') : 'N/A',
            severityLevel: tick.severityLevel || 'medium'
        }));
    }, [suspiciousTicks?.data]);

    // Get total count from API response or fallback to processed data length
    const totalCount = suspiciousTicks?.totalCount || suspiciousTicks?.total || processedData.length;

    // For server-side pagination, use processedData directly (no client-side slicing)
    // For client-side pagination, use slicing
    const paginatedData = processedData;

    // Get filter counts for display with memoization
    const filterCount = useMemo(() => {
        return Object.keys(appliedFilters).filter(key => appliedFilters[key]).length;
    }, [appliedFilters]);

    return (
        <Page title="Suspicious Ticks Analysis">
            <Container maxWidth={themeStretch ? false : 'xl'}>
                <HeaderBreadcrumbs
                    // heading="Suspicious Ticks Analysis"
                    heading=""
                    links={[
                        { name: 'Dashboard', href: '/dashboard' },
                        { name: 'Suspicious Ticks Analysis' },
                    ]}
                    action={
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Tooltip title="Alert Types Information">
                                <IconButton
                                    onClick={() => setInfoModalOpen(true)}
                                    color="info"
                                    size="small"
                                    sx={{
                                        backgroundColor: 'info.lighter',
                                        '&:hover': { backgroundColor: 'info.light' }
                                    }}
                                >
                                    <InfoIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Refresh Data">
                                <span>
                                    <IconButton
                                        onClick={handleRefresh}
                                        disabled={isLoading}
                                        size="small"
                                        sx={{
                                            backgroundColor: 'primary.lighter',
                                            '&:hover': { backgroundColor: 'primary.light' }
                                        }}
                                    >
                                        <RefreshIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Badge
                                variant="dot"
                                color="primary"
                                invisible={filterCount === 0}
                            >
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<FilterIcon fontSize="small" />}
                                    onClick={() => setFiltersOpen(true)}
                                    sx={{
                                        fontSize: '0.875rem',
                                        minWidth: 'auto',
                                        px: 2
                                    }}
                                >
                                    Filters {filterCount > 0 && `(${filterCount})`}
                                </Button>
                            </Badge>
                        </Stack>
                    }
                />

                {/* Active filters indicator */}
                {filterCount > 0 && (
                    <Card sx={{ mb: 2, backgroundColor: 'primary.lighter' }}>
                        <Box sx={{ px: 3, py: 1.5 }}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between">
                                <Typography variant="body2" color="primary.dark" fontSize="0.875rem">
                                    <strong>{filterCount}</strong> filter(s) applied
                                </Typography>
                                <Button
                                    size="small"
                                    onClick={() => handleApplyFilters({})}
                                    sx={{ fontSize: '0.75rem', minWidth: 'auto' }}
                                >
                                    Clear All
                                </Button>
                            </Stack>
                        </Box>
                    </Card>
                )}

                {/* Professional Table */}
                <Card
                    sx={{
                        boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
                        borderRadius: 2,
                        overflow: 'hidden'
                    }}
                >
                    <Box
                        sx={{
                            px: 1.5,
                            py: 0.8,
                            backgroundColor: 'grey.50',
                            borderBottom: '1px solid',
                            borderColor: 'divider'
                        }}
                    >
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Typography
                                variant="h8"
                                fontSize="1rem"
                                fontWeight="600"
                                color="text.primary"
                            >
                                Suspicious Ticks ({totalCount} records)
                            </Typography>
                            {isLoading && (
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <CircularProgress size={16} />
                                    <Typography variant="body2" fontSize="0.875rem" color="text.secondary">
                                        Refreshing...
                                    </Typography>
                                </Stack>
                            )}
                        </Stack>
                    </Box>

                    <Scrollbar>
                        <TableContainer>
                            <Table
                                sx={{
                                    borderCollapse: 'separate',
                                    borderSpacing: 0,
                                    border: '1px solid',
                                    borderColor: 'grey.400',
                                    '& .MuiTableHead-root': {
                                        backgroundColor: 'grey.100'
                                    },
                                    '& .MuiTableCell-head': {
                                        fontSize: '0.75rem',
                                        fontWeight: 600,
                                        color: 'text.primary',
                                        borderBottom: '2px solid',
                                        borderRight: '1px solid',
                                        borderColor: 'grey.400',
                                        py: 1,
                                        '&:last-child': {
                                            borderRight: 'none'
                                        }
                                    },
                                    '& .MuiTableCell-body': {
                                        borderBottom: '1px solid',
                                        borderRight: '1px solid',
                                        borderColor: 'grey.400',
                                        fontSize: '0.75rem',
                                        '&:last-child': {
                                            borderRight: 'none'
                                        }
                                    },
                                    '& .MuiTableRow-root:last-child .MuiTableCell-body': {
                                        borderBottom: '1px solid',
                                        borderColor: 'grey.400'
                                    }
                                }}
                            ><TableHeadCustom headLabel={TABLE_HEAD} /><TableBody>
                                    {renderTableContent(isLoading, paginatedData, TABLE_HEAD, getAlertTypeColor, getSeverityColor, handleRowClick)}
                                </TableBody></Table>
                        </TableContainer>
                    </Scrollbar>

                    <Box
                        sx={{
                            borderTop: '1px solid',
                            borderColor: 'divider',
                            backgroundColor: 'grey.50'
                        }}
                    >
                        <TablePagination
                            rowsPerPageOptions={[50, 100, 300, 500]}
                            component="div"
                            count={totalCount}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            sx={{
                                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                                    fontSize: '0.875rem'
                                },
                                '& .MuiTablePagination-select': {
                                    fontSize: '0.875rem'
                                }
                            }}
                        />
                    </Box>
                </Card>

                {/* Filters Modal */}
                <SuspiciousTicksFiltersModal
                    open={filtersOpen}
                    onClose={() => setFiltersOpen(false)}
                    onApply={handleApplyFilters}
                    currentFilters={appliedFilters}
                />

                {/* Info Modal */}
                <AlertTypesInfoModal
                    open={infoModalOpen}
                    onClose={() => setInfoModalOpen(false)}
                />

                {/* Details Modal */}
                <SuspiciousTickDetailsModal
                    open={detailsModalOpen}
                    onClose={() => setDetailsModalOpen(false)}
                    tick={selectedTick}
                />
            </Container>
        </Page>
    );
};

export default SourceSuspiciousTicksList;
